import { isValid, parse as parseDate } from 'date-fns';

/**
 * Tolerant parser for hand-typed dates (ENG-837).
 *
 * The `DatePicker` input displays whatever date-fns format its consumer
 * configures — and on the planner grids that format is partner-configurable
 * (`dd/MM/yyyy`, `yyyy-MM-dd`, `EEE do MMM yyyy`, …). Requiring users to
 * retype the display format exactly makes fast data entry painful, so a
 * purely numeric entry is mapped onto the format's own field ORDER instead
 * of its literal punctuation:
 *
 *   - `15-08` under `dd/MM/yyyy` → 15 August of the reference year
 *   - `08/15` under `MM/dd/yyyy` → 15 August of the reference year
 *   - `05-20` under `yyyy-MM-dd` → 20 May of the reference year
 *   - `15/08/26` under any day-first format → 15 August 2026
 *
 * Anything that isn't all-numeric (named months, ordinals, weekday prefixes)
 * falls through to a strict date-fns parse against the configured format, so
 * "3rd Aug 2026" and "Mon 28th Jul 2026" keep working unchanged.
 */

/** Accepted separators between the numeric parts: `/`, `-`, `.` or spaces. */
const SEPARATOR = /[/\-.\s]+/;

const DAY_OR_MONTH_PART = /^\d{1,2}$/;
/** A year is typed either short (`26`) or in full (`2026`) — never 3 digits. */
const YEAR_PART = /^(\d{1,2}|\d{4})$/;

type DateField = 'day' | 'month' | 'year';

/**
 * The order in which day, month and year appear in a date-fns format string.
 * Quoted literals are stripped first so an escaped `d`/`M`/`y` inside one
 * can't be mistaken for a token.
 *
 *   `dd/MM/yyyy`      → ['day', 'month', 'year']
 *   `yyyy-MM-dd`      → ['year', 'month', 'day']
 *   `EEE do MMM yyyy` → ['day', 'month', 'year']
 *   `dd/MM`           → ['day', 'month']
 */
export function dateFieldOrder(format: string): DateField[] {
  const bare = format.replace(/'[^']*'/g, '');
  const found: { field: DateField; at: number }[] = [];
  // d / dd / do — day of month. M / MM / MMM / MMMM — month. y / yy / yyyy — year.
  for (const [field, token] of [
    ['day', 'd'],
    ['month', 'M'],
    ['year', 'y'],
  ] as const) {
    const at = bare.indexOf(token);
    if (at !== -1) found.push({ field, at });
  }
  return found.sort((a, b) => a.at - b.at).map((entry) => entry.field);
}

/** Short years are always this century — `26` → 2026. */
function expandYear(year: number): number {
  return year < 100 ? 2000 + year : year;
}

/**
 * Map an all-numeric entry onto the format's field order. Returns null when
 * the entry isn't numeric shorthand at all, or when it doesn't describe a
 * real calendar day (31-02, month 13, …) — the caller then leaves the cell's
 * existing value alone rather than committing a guess.
 */
function parseNumericShorthand(text: string, format: string, referenceDate: Date): Date | null {
  const parts = text.split(SEPARATOR);
  if (parts.length < 2 || parts.length > 3) return null;

  const order = dateFieldOrder(format);
  if (!order.includes('day') || !order.includes('month')) return null;

  // Two parts is the day + month shorthand — drop the year slot and take the
  // year from the reference date. Three parts always ends with an explicit
  // year, even for a year-less display format like `dd/MM`.
  const fields: DateField[] =
    parts.length === 3
      ? order.includes('year')
        ? order
        : [...order, 'year']
      : order.filter((field) => field !== 'year');
  if (fields.length !== parts.length) return null;

  const values = new Map<DateField, number>();
  for (const [index, field] of fields.entries()) {
    const part = parts[index]!;
    const pattern = field === 'year' ? YEAR_PART : DAY_OR_MONTH_PART;
    if (!pattern.test(part)) return null;
    values.set(field, Number(part));
  }

  const day = values.get('day')!;
  const month = values.get('month')!;
  const shortYear = values.get('year');
  const year = shortYear === undefined ? referenceDate.getFullYear() : expandYear(shortYear);

  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const date = new Date(year, month - 1, day);
  // Reject days that don't exist in the month (JS rolls 31 Feb into March).
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

/**
 * Parse typed date text against a date-fns `format`, accepting numeric
 * shorthand first and falling back to a strict parse of the full format.
 * Returns null when the text describes no valid date.
 */
export function parseDateInput(
  text: string,
  format: string,
  referenceDate: Date = new Date(),
): Date | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;

  const shorthand = parseNumericShorthand(trimmed, format, referenceDate);
  if (shorthand) return shorthand;

  const parsed = parseDate(trimmed, format, referenceDate);
  return isValid(parsed) ? parsed : null;
}
